import React, { useEffect, useState } from 'react';
import { fetchAuthenticatedFile } from '../utils/authenticatedFile';

export type FileViewerModalProps = {
  /** URL do arquivo (object URL de blob ou href direto). */
  url: string;
  /** Nome exibido no cabeçalho. */
  nome: string;
  /** Usado para decidir entre <img> e <iframe>. */
  mimeType?: string | null;
  /** Linha secundária no cabeçalho (rótulo do grupo, código, posição, etc.). */
  subtitle?: React.ReactNode;
  /** Href alternativo do botão "Baixar"; por padrão usa o arquivo autenticado carregado. */
  downloadUrl?: string;
  onClose: () => void;
  /** Navegação opcional (quando há vários documentos). */
  onPrev?: () => void;
  onNext?: () => void;
  canPrev?: boolean;
  canNext?: boolean;
};

const navBtn =
  'inline-flex items-center rounded border border-gray-600 bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40';

/**
 * Visualizador de arquivo em overlay fullscreen (imagem ou PDF em iframe).
 * Componente único reutilizado por todas as telas — não duplicar inline.
 */
const FileViewerModal: React.FC<FileViewerModalProps> = ({
  url,
  nome,
  mimeType,
  subtitle,
  downloadUrl,
  onClose,
  onPrev,
  onNext,
  canPrev,
  canNext,
}) => {
  const [loadedFile, setLoadedFile] = useState<{
    sourceUrl: string;
    objectUrl: string;
    mimeType: string;
    error: boolean;
  } | null>(null);

  const target = url ? new URL(url, window.location.origin) : null;
  const usesDirectUrl = Boolean(
    target && (url.startsWith('blob:') || url.startsWith('data:') || target.origin !== window.location.origin)
  );

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = '';

    if (!url || usesDirectUrl) return () => controller.abort();

    fetchAuthenticatedFile(url, controller.signal)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setLoadedFile({
          sourceUrl: url,
          objectUrl,
          mimeType: blob.type || mimeType || '',
          error: false,
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoadedFile({ sourceUrl: url, objectUrl: '', mimeType: mimeType || '', error: true });
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, mimeType, usesDirectUrl]);

  const currentFile = loadedFile?.sourceUrl === url ? loadedFile : null;
  const resolvedUrl = usesDirectUrl ? url : currentFile?.objectUrl ?? '';
  const resolvedMimeType = usesDirectUrl ? mimeType ?? '' : currentFile?.mimeType ?? mimeType ?? '';
  const loading = Boolean(url && !usesDirectUrl && !currentFile);
  const loadError = currentFile?.error ?? false;
  const baixarHref = downloadUrl ?? resolvedUrl;
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900/90">
      <div className="flex items-center justify-between gap-4 border-b border-gray-700 bg-gray-900 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{nome}</p>
          {subtitle && <p className="text-xs text-fg-subtle">{subtitle}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onPrev && (
            <button type="button" disabled={!canPrev} onClick={onPrev} className={navBtn}>
              ← Anterior
            </button>
          )}
          {onNext && (
            <button type="button" disabled={!canNext} onClick={onNext} className={navBtn}>
              Próximo →
            </button>
          )}
          {baixarHref ? (
            <a
              href={baixarHref}
              download={nome}
              className="inline-flex items-center rounded border border-gray-600 bg-gray-800 px-3 py-1.5 text-sm text-white hover:bg-gray-700"
            >
              Baixar
            </a>
          ) : (
            <span className={`${navBtn} cursor-not-allowed opacity-40`}>Baixar</span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded border border-red-700 bg-red-800 px-3 py-1.5 text-sm text-white hover:bg-red-700"
          >
            Fechar ✕
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex h-full items-center justify-center text-gray-300">Carregando arquivo...</div>
        ) : loadError ? (
          <div className="flex h-full items-center justify-center text-gray-300">
            Não foi possível carregar o arquivo.
          </div>
        ) : resolvedMimeType.startsWith('image/') ? (
          <img src={resolvedUrl} alt={nome} className="mx-auto h-full max-h-full object-contain" />
        ) : resolvedUrl ? (
          <iframe src={resolvedUrl} title={nome} className="h-full w-full border-0" />
        ) : (
          <div className="flex h-full items-center justify-center text-gray-300">
            <div className="text-center">
              <p className="mb-4 text-base font-semibold">Arquivo não disponível para visualização</p>
              <a
                href={baixarHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Baixar arquivo
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileViewerModal;
